import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";

import useFinanceContas from "@/hooks/useFinanceContas";
import HeroSaldoContasCard from "./contas/HeroSaldoContasCard";
import ContasToolbar from "./contas/ContasToolbar";
import KpisCreditoRow from "./contas/KpisCreditoRow";
import AlertBanners from "./contas/AlertBanners";
import CartoesGrid from "./contas/CartoesGrid";
import DefinirDatasModal from "./contas/DefinirDatasModal";
import GastosCartoesBarChart from "./contas/GastosCartoesBarChart";
import GastosPorCartaoDonut from "./contas/GastosPorCartaoDonut";
import ContasBancariasSection from "./contas/ContasBancariasSection";
import NexoProactiveAlert from "./contas/NexoProactiveAlert";

// FIN-3 — ContasTab orquestrador.
// Hierarquia: Hero → Toolbar → KPIs → Banners (T8) → Cartões (T9-T10)
// → Gráficos (T11) → Contas Bancárias (T12).

const ROW_TRANSITION = { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] };

function _currentMonthIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function ContasTab({ onTalk }) {
  const [referenceMonth, setReferenceMonth] = useState(_currentMonthIso());
  const [definirDatasFor, setDefinirDatasFor] = useState(null);
  const c = useFinanceContas({ referenceMonth });

  // Listener pra eventos do banner / dos botões edit (T8/T9 disparam).
  useEffect(() => {
    const onOpen = (ev) => {
      const id = ev?.detail?.accountId;
      if (!id) return;
      const acc = c.accounts.find(a => a.id === id);
      if (acc) setDefinirDatasFor(acc);
    };
    window.addEventListener("contas:open-definir-datas", onOpen);
    return () => window.removeEventListener("contas:open-definir-datas", onOpen);
  }, [c.accounts]);

  const openDefinirDatas = useCallback((acc) => setDefinirDatasFor(acc), []);

  return (
    <div className="space-y-5 md:space-y-6" data-testid="finance-contas">
      {/* Hero saldo adaptativo OF ON / OFF */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ROW_TRANSITION}
      >
        <HeroSaldoContasCard
          totalBalance={c.totalBalance}
          creditCards={c.creditCards}
          bankAccounts={c.bankAccounts}
          isOpenFinance={c.isOpenFinance}
          distinctInstitutions={c.distinctInstitutions}
          loading={c.isLoading}
        />
      </motion.section>

      {/* Toolbar (navegador de mês) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.08 }}
      >
        <ContasToolbar
          referenceMonth={referenceMonth}
          onChangeMonth={setReferenceMonth}
        />
      </motion.section>

      {/* 2 KPIs (Limite total + Crédito usado, com glow contextual) */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.14 }}
      >
        <KpisCreditoRow
          creditUsage={c.creditUsage}
          creditCardsCount={c.creditCards.length}
        />
      </motion.section>

      {/* Banner Nexo proativo (vencimento iminente / uso crítico de crédito) */}
      <NexoProactiveAlert
        creditCards={c.creditCards}
        creditUsage={c.creditUsage}
        onTalk={onTalk}
      />

      {/* Banners contextuais (cartão sem data + fatura estimada) */}
      <AlertBanners
        creditCards={c.creditCards}
        hasFaturaEstimada={c.creditCards.some(card => card.closing_day && card.due_day)}
        onConfigureDates={openDefinirDatas}
      />

      {/* Grid de cartões com hover actions + acordeão de detalhes */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.20 }}
      >
        <CartoesGrid
          creditCards={c.creditCards}
          creditUsage={c.creditUsage}
          isLoading={c.isLoading}
          onEditDates={openDefinirDatas}
          onDelete={async (card) => {
            if (window.confirm(`Apagar cartão "${card.name}"? Essa ação não pode ser desfeita.`)) {
              try { await c.deleteAccount(card.id); } catch (e) { /* hook trata error */ }
            }
          }}
          onTalk={(card) => {
            if (onTalk) {
              const prompt = `Como está minha fatura do ${card.name} este mês? Tem alguma transação atípica?`;
              onTalk(prompt);
            }
          }}
          onSeeTransactions={(card) => {
            // Navega pra Extrato com pre-filtro de account_id
            const params = new URLSearchParams();
            params.append("accounts", card.id);
            const newHash = "financeiro/extrato";
            const newSearch = params.toString();
            const url = window.location.pathname + (newSearch ? `?${newSearch}` : "") + `#${newHash}`;
            window.history.pushState(null, "", url);
            window.location.hash = newHash;  // dispara hashchange listener da FinanceSection
          }}
        />
      </motion.section>

      {/* 2 gráficos (BarChart 6m + Donut por cartão) — só renderiza se houver cartões */}
      {c.creditCards.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...ROW_TRANSITION, delay: 0.26 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6"
        >
          <GastosCartoesBarChart creditCards={c.creditCards} />
          <GastosPorCartaoDonut spendingByCard={c.spendingByCard} />
        </motion.section>
      )}

      {/* Lista contas bancárias com CRUD */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...ROW_TRANSITION, delay: 0.32 }}
      >
        <ContasBancariasSection
          bankAccounts={c.bankAccounts}
          isLoading={c.isLoading}
          createAccount={c.createAccount}
          updateAccount={c.updateAccount}
          deleteAccount={c.deleteAccount}
        />
      </motion.section>

      {c.error && (
        <div
          className="text-caption text-status-error px-2"
          data-testid="contas-error"
        >
          {c.error}
        </div>
      )}

      {/* Modal Definir datas — controlled */}
      <DefinirDatasModal
        open={Boolean(definirDatasFor)}
        onOpenChange={(o) => { if (!o) setDefinirDatasFor(null); }}
        account={definirDatasFor}
        onSave={c.updateAccount}
      />
    </div>
  );
}
